import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Public page required for Stripe Connect platforms — the "Seller Risk
// Explainer". Stripe expects the platform to publish, in plain language,
// what risks a seller (host, restaurant, driver, service provider) takes
// on by using the platform: chargebacks, refunds, payouts, disputes.

const SellerPaymentsAndRiskPage = () => {
  const sectionHeadingClass = 'text-xl md:text-2xl font-bold text-[#1F3A68] mt-8 mb-3';
  const paragraphClass = 'text-slate-700 leading-relaxed mb-3';
  const listClass = 'list-disc pl-6 space-y-1 text-slate-700 mb-3';

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-10">
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F3A68] mb-2">
            Payments and Seller Risk
          </h1>
          <p className="text-sm text-slate-500 mb-8">Last updated: 13 May 2026</p>

          <section className="mb-6">
            <p className={paragraphClass}>
              This page explains, in plain English, how money moves through Ariv and the
              risks you take on as a host, restaurant, driver, or service provider. Read
              this once at the start — it answers most questions about payouts, refunds,
              and chargebacks. The detailed legal terms are in our{' '}
              <Link to="/terms" className="underline text-[#1F3A68]">
                Terms of Service
              </Link>
              .
            </p>
          </section>

          <h2 className={sectionHeadingClass}>1. How money flows</h2>
          <p className={paragraphClass}>
            Ariv is a marketplace. The guest, customer, or rider pays{' '}
            <strong>Ariv</strong> at the point of booking through Stripe Checkout. We
            keep our platform fee and forward the rest to your Stripe Connect account.
            Once a week, the balance in your Connect account is paid out to your nominated
            UK bank account.
          </p>
          <ul className={listClass}>
            <li>
              <strong>Stripe</strong> is our payment processor. We do not see or store
              your card details or your customer's card details.
            </li>
            <li>
              You are <strong>onboarded</strong> through Stripe Connect (Express) and
              hold your own Stripe-managed account with Ariv as your platform.
            </li>
            <li>
              <strong>Payouts</strong> happen weekly on Mondays (UK time) for the
              previous week's earnings.
            </li>
            <li>
              <strong>Currency</strong> is GBP throughout. We don't currently support
              multi-currency.
            </li>
          </ul>

          <h2 className={sectionHeadingClass}>2. Our fees</h2>
          <p className={paragraphClass}>
            Standard Ariv platform fees are <strong>13% of accommodation revenue</strong>{' '}
            for hosts under the host only model (the guest pays no separate platform
            fee), with separate rates published per vertical for restaurants, drivers,
            and service providers. Founding cohort hosts (the first 100 hosts) pay{' '}
            <strong>0% for their first 90 days</strong>, then <strong>10% for the rest
            of their first year</strong>, then the standard rate, with the founder badge
            kept permanently.
          </p>
          <p className={paragraphClass}>
            On top of our platform fee, Stripe charges its own card-processing fee
            (typically <strong>1.5% + 20p</strong> for UK cards and{' '}
            <strong>2.5% + 20p</strong> for non-UK cards). This is deducted from the
            transaction before the host share is calculated.
          </p>

          <h2 className={sectionHeadingClass}>3. Refunds and cancellations</h2>
          <p className={paragraphClass}>
            Each property listing carries a cancellation policy that you, the host,
            choose at the time of listing. There are five tiers modelled on the standard
            short-term-let market:
          </p>
          <ul className={listClass}>
            <li>
              <strong>Flexible</strong> — full refund up to 24h before check-in.
            </li>
            <li>
              <strong>Moderate</strong> — full refund up to 5 days before check-in.
            </li>
            <li>
              <strong>Firm</strong> — full refund up to 30 days, 50% inside 7–30 days,
              none after.
            </li>
            <li>
              <strong>Strict</strong> — 50% refund up to 7 days before, none after.
            </li>
            <li>
              <strong>Long-term</strong> — first month non-refundable; 30-day notice
              thereafter.
            </li>
          </ul>
          <p className={paragraphClass}>
            When a guest cancels, the refund is calculated automatically against the
            policy that was in effect when they booked. If your weekly payout has already
            moved the host share to your Connect balance, we reverse the refunded
            portion. If you have already withdrawn it to your bank, we'll contact you to
            arrange recovery.
          </p>
          <p className={paragraphClass}>
            You can also offer a discretionary full refund at any time from your
            dashboard — useful for goodwill, host-side issues, or extraordinary
            circumstances.
          </p>

          <h2 className={sectionHeadingClass}>4. Chargebacks</h2>
          <p className={paragraphClass}>
            A chargeback happens when a guest disputes a transaction directly with their
            card issuer. The card network (Visa, Mastercard) holds the funds while the
            dispute is investigated, and if the guest wins, the money is taken back from
            Ariv's account — and from you, if your share has already been paid out.
          </p>
          <ul className={listClass}>
            <li>
              We are notified of every chargeback immediately and pass the notice to you
              the same day.
            </li>
            <li>
              We will work with you to assemble the evidence (messaging records, photos,
              guest agreement, property condition reports) to defend the dispute within
              Stripe's evidence window — usually 7–21 days.
            </li>
            <li>
              If the dispute is lost, the chargeback amount and Stripe's <strong>£15</strong>{' '}
              dispute fee are deducted from your future earnings.
            </li>
            <li>
              Persistent or unaddressed chargeback patterns will result in account review
              and possible suspension.
            </li>
          </ul>

          <h2 className={sectionHeadingClass}>5. Payout failures</h2>
          <p className={paragraphClass}>
            Weekly payouts can fail if your bank rejects the transfer — usually because
            of incorrect bank details, a closed account, or your bank flagging the
            payment as suspicious. We are notified immediately and email you the same
            day. You'll need to update your details in your Stripe Connect dashboard;
            we'll retry the payout once it's fixed.
          </p>

          <h2 className={sectionHeadingClass}>6. VAT</h2>
          <p className={paragraphClass}>
            Brick &amp; Capital Ltd is not currently VAT-registered, so no VAT is charged
            on Ariv's platform fees today. Once we cross the UK VAT registration
            threshold and register, VAT will be added to platform fees and invoices, and
            we'll publish a VAT registration number at that point.
          </p>
          <p className={paragraphClass}>
            <strong>Your own VAT position is your responsibility.</strong> If you are a
            VAT-registered business letting property, running a restaurant, or operating
            a vehicle, you must add and report VAT on your side as required by HMRC. We
            recommend speaking to an accountant.
          </p>

          <h2 className={sectionHeadingClass}>7. Account holds and reserves</h2>
          <p className={paragraphClass}>
            In limited circumstances we may temporarily hold a payout — for example if a
            booking is under dispute, if there's an open chargeback, or if your account
            has a compliance document about to expire. We'll always email you the reason
            and the steps to release the hold.
          </p>
          <p className={paragraphClass}>
            Stripe may independently impose a reserve on your Connect account during
            their own risk review (usually the first few weeks of activity for new
            sellers, or after a sudden change in volume). This is Stripe's call and
            visible in your Stripe Express dashboard.
          </p>

          <h2 className={sectionHeadingClass}>8. Tax reporting</h2>
          <p className={paragraphClass}>
            You are the seller of record for your own services. You're responsible for
            declaring your Ariv earnings to HMRC and paying any income tax, National
            Insurance, or Corporation Tax due on them. We will provide an annual
            earnings summary to make your tax return easier; we don't withhold tax on
            your behalf.
          </p>

          <h2 className={sectionHeadingClass}>9. Disputes between you and Ariv</h2>
          <p className={paragraphClass}>
            If you disagree with a refund decision, a chargeback outcome, an account
            hold, or any other action we take, please email{' '}
            <strong>support@ariv.co.uk</strong>. We'll respond within 5 working days
            with a written decision. If you remain dissatisfied, you can escalate to an
            Alternative Dispute Resolution provider — details will be added here once
            we've finalised the scheme membership.
          </p>

          <h2 className={sectionHeadingClass}>10. Where to find your numbers</h2>
          <ul className={listClass}>
            <li>
              <strong>Earnings and ledger:</strong>{' '}
              <Link to="/host/dashboard" className="underline text-[#1F3A68]">
                Host dashboard → Payouts
              </Link>
            </li>
            <li>
              <strong>Cancellation breakdowns:</strong> shown automatically against each
              cancelled booking in your dashboard.
            </li>
            <li>
              <strong>Stripe Connect:</strong> tax forms, KYC, bank details, and the raw
              transaction feed live in your Stripe Express dashboard. Use the "View on
              Stripe" link from your Ariv dashboard.
            </li>
          </ul>

          <div className="mt-10 pt-6 border-t border-slate-200 text-sm text-slate-500">
            <Link to="/terms" className="underline text-[#1F3A68]">
              Terms of Service
            </Link>{' · '}
            <Link to="/privacy" className="underline text-[#1F3A68]">
              Privacy Policy
            </Link>{' · '}
            <Link to="/restricted-businesses" className="underline text-[#1F3A68]">
              Restricted businesses
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SellerPaymentsAndRiskPage;
